import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  makeStyles,
  List,
  ListItem,
  Divider,
  Tooltip
} from '@material-ui/core';
import {
  ExpandMore,
  ChevronRight,
  DirectionsCar,
  Business,
  Person,
  MoreVert
} from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  getListVehicleTrackingById,
  getListVehicleTrackingByUserId,
  setListVehicleTracking,
  setStatisticVehicleStatus
} from 'src/features/vehicleSlice';
import { getListAgencies } from 'src/features/agencySlice';
import { setTimeSearching } from 'src/features/deviceSlice';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { useQuery } from 'src/app/hooks/useQuery';
import { deduplicatedFetch } from 'src/app/utils/requestDeduplicator';

import { getIconStatus } from 'src/app/utils/vehicleService';
import { reportSubmenu, roadSubmenu } from './constance';

const useStyles = makeStyles(theme => ({
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '5px 10px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  nodeRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
    gap: 6
  },
  vehicleHover: {
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f2f2f2'
    }
  },
  activeRow: {
    backgroundColor: '#ffe4e1 !important'
  }
}));

const TreeNode = ({
  node,
  onClick,
  rowActive,
  getVehicle,
  activeAgencyId,
  activeEndUserId,
  showVehiclesInTree = true
}) => {
  const [menuPosition, setMenuPosition] = useState(null);
  const [menuRowIndex, setMenuRowIndex] = useState(null);
  const [vehicleSub, setVehicleSub] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [menuType, setMenuType] = useState([]);
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nodeRef = useRef(null);
  const nameRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const isAgency = node?.type === 'agency';
  const isEndUser = node?.type === 'end_user';
  const isVehicle = node?.type === 'vehicle';
  const vehicle = node?.data;

  const isActive =
    (isVehicle &&
      rowActive &&
      (node?.id == rowActive ||
        vehicle?.device_id == rowActive ||
        vehicle?.id == rowActive)) ||
    (isAgency && activeAgencyId && node?.id == activeAgencyId) ||
    (isEndUser && activeEndUserId && node?.endUserId == activeEndUserId);

  useEffect(() => {
    if (isActive && nodeRef.current) {
      nodeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (nameRef.current) {
      setIsOverflowing(
        nameRef.current.scrollWidth > nameRef.current.clientWidth
      );
    }
  }, [
    node.name,
    vehicle?.name,
    vehicle?.license_plate,
    vehicle?.vehicle?.license_plate
  ]);

  if (!node) return null;

  const isExpandable =
    (isAgency || (isEndUser && showVehiclesInTree)) &&
    (node.count === undefined ||
      node.count > 0 ||
      (node.loaded && node.children && node.children.length > 0));

  const hasChildren = node.children && node.children.length > 0;

  const vehicleIcon = isVehicle
    ? vehicle?.debt === true
      ? '/static/iconSvg/motors/motor_expired.svg'
      : getIconStatus(vehicle?.device_status, vehicle?.vehicle_type)
    : null;

  const handleRowClick = vehicle => {
    setMenuPosition(null);
    if (vehicle.device_id === rowActive) {
      getVehicle(null);
    } else {
      getVehicle(vehicle);
    }
  };

  const handleClickMenu = (event, rowIndex, vehicle) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    let top = rect.bottom - 134;
    let left = rect.left + 38;

    setMenuPosition({ top, left });
    setMenuRowIndex(rowIndex);
    setVehicleSub(vehicle);
  };

  const handleCloseMenu = () => {
    setMenuRowIndex(null);
    setMenuPosition(null);
  };

  const handleSubmenuOpen = (event, type) => {
    const rect = event.currentTarget.getBoundingClientRect();
    let top = rect.top;
    let left = rect.right;
    setSubmenuPosition({ top, left });
    setSubmenuOpen(true);
    setMenuType(type === 'report' ? reportSubmenu : roadSubmenu);
  };

  const cameraIcon =
    vehicle?.device_status === 'moving'
      ? '/static/iconSvg/video_motion_active.svg'
      : '/static/iconSvg/video_motion.svg';

  const handleSubmenuClose = () => {
    setSubmenuOpen(false);
  };

  const isCar4G = isVehicle;
  const isCamera4G = vehicle?.device_type === 'VGPS_2CAM' || vehicle?.device_type === 'VGPS_4CAM';

  const handleQuickViewRoute = (e, menuItem) => {
    e.stopPropagation();
    if (menuItem.type === 'ROAD') {
      dispatch(
        setTimeSearching({
          id: vehicleSub.id || vehicleSub.device_id,
          license_plate:
            vehicleSub.vehicle?.license_plate || vehicleSub.license_plate,
          data: menuItem
        })
      );
    }
    navigate(`${menuItem.url}`);
    handleCloseMenu();
  };

  return (
    <Box>
      <Box
        ref={nodeRef}
        onClick={() => onClick(node)}
        className={`
          ${classes.nodeRow} 
          ${isVehicle ? classes.vehicleHover : ''} 
          ${isActive ? classes.activeRow : ''}
        `}
        style={{
          cursor: 'pointer',
          pointerEvents: 'auto'
        }}
      >
        {/* expand icon */}
        {isExpandable ? (
          node.open ? (
            <ExpandMore fontSize="small" />
          ) : (
            <ChevronRight fontSize="small" />
          )
        ) : (
          <Box style={{ width: 20 }} />
        )}

        {/* agency icon */}
        {isAgency && (
          <Business
            style={{
              fontSize: 16,
              marginRight: 6,
              color: '#d9042f'
            }}
          />
        )}

        {/* end user icon */}
        {isEndUser && (
          <Person
            style={{
              fontSize: 16,
              marginRight: 6,
              color: '#d9042f'
            }}
          />
        )}

        {/* vehicle icon */}
        {isVehicle && (
          <Box
            style={{
              width: 20,
              height: 20,
              marginRight: 6
            }}
          >
            <img
              src={vehicleIcon}
              alt="vehicle"
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        )}
        <Box
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          <Tooltip
            title={
              isVehicle
                ? vehicle?.is_display_name && vehicle?.name
                  ? vehicle.name
                  : vehicle?.license_plate ||
                    vehicle?.vehicle?.license_plate ||
                    node.name
                : node.name
            }
            disableHoverListener={!isOverflowing}
            arrow
          >
            <Typography
              ref={nameRef}
              style={{
                fontSize: 13,
                fontWeight: isVehicle ? 500 : 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                textTransform: 'capitalize'
              }}
            >
              {isVehicle
                ? vehicle?.is_display_name && vehicle?.name
                  ? vehicle.name
                  : vehicle?.license_plate ||
                    vehicle?.vehicle?.license_plate ||
                    node.name
                : node.name}
            </Typography>
          </Tooltip>
          {node.count !== undefined && !isVehicle && (
            <span style={{ marginLeft: 4, fontWeight: 600, flexShrink: 0 }}>
              ({node.activeCount !== undefined ? node.activeCount : node.count}/
              {node.count})
            </span>
          )}
        </Box>

        {/* camera icon */}
        {isVehicle && (vehicle?.device_type === 'VGPS_2CAM' || vehicle?.device_type === 'VGPS_4CAM') && (
          <Box
            style={{
              width: 14,
              height: 10
            }}
          >
            <img
              src={cameraIcon}
              alt="camera"
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        )}
        {node.loading && (
          <CircularProgress size={14} style={{ marginLeft: 6 }} />
        )}

        {isVehicle && (
          <Box
            onClick={e => handleClickMenu(e, node.id, vehicle)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 4px',
              cursor: 'pointer'
            }}
          >
            <MoreVert fontSize="small" style={{ color: '#666' }} />
          </Box>
        )}
      </Box>

      {menuPosition && (
        <div
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            backgroundColor: '#fff',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
            zIndex: 9999,
            minWidth: '140px',
            borderRadius: '4px'
          }}
          onMouseLeave={() => {
            handleCloseMenu();
            handleSubmenuClose();
          }}
        >
          <List style={{ fontSize: '14px', padding: 0 }}>
            <ListItem
              className={classes.menuItem}
              onMouseEnter={e => handleSubmenuOpen(e, 'road')}
              style={{ padding: '5px 10px' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <span>Xem lộ trình</span>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    height: '20px',
                    width: '20px'
                  }}
                >
                  <img alt="arrow" src="/static/iconSvg/arrow_right.svg" />
                </span>
              </div>
            </ListItem>

            <Divider />
            <ListItem
              className={classes.menuItem}
              onMouseEnter={e => handleSubmenuOpen(e, 'report')}
              style={{
                padding: '5px 10px',
                pointerEvents: !isCar4G ? 'none' : 'auto'
              }}
              disabled={!isCar4G}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <span>Báo cáo</span>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    height: '20px',
                    width: '20px'
                  }}
                >
                  <img alt="arrow" src="/static/iconSvg/arrow_right.svg" />
                </span>
              </div>
            </ListItem>

            <Divider />
            <ListItem
              className={classes.menuItem}
              onClick={() => navigate('/app/image')}
              style={{ padding: '5px 10px' }}
              disabled={!isCamera4G}
            >
              <span>Xem hình ảnh</span>
            </ListItem>

            <Divider />
            <ListItem
              className={classes.menuItem}
              onClick={() => navigate('app/camera?page=1')}
              style={{ padding: '5px 10px' }}
              disabled={!isCamera4G}
            >
              <span>Xem lại video</span>
            </ListItem>
          </List>

          {submenuOpen && submenuPosition && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 141,
                backgroundColor: '#fff',
                boxShadow: '0px 1px 10px rgba(36, 36, 36, 0.2)',
                zIndex: 1000,
                minWidth: '200px',
                borderRadius: '4px'
              }}
              onMouseLeave={handleSubmenuClose}
              onMouseEnter={() => setSubmenuOpen(true)}
            >
              <List style={{ fontSize: '14px', padding: 0 }}>
                {menuType.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      className={classes.menuItem}
                      onClick={e => handleQuickViewRoute(e, item)}
                      style={{
                        padding: '5px 10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span>{item.name}</span>
                      {item.type === 'ROAD' && (
                        <span>
                          <img alt="clock" src="/static/iconSvg/clock_raw.svg" />
                        </span>
                      )}
                    </ListItem>
                    {index !== menuType.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </div>
          )}
        </div>
      )}

      {/* children */}
      {node.open && hasChildren && (
        <Box style={{ paddingLeft: 16 }}>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              onClick={onClick}
              rowActive={rowActive}
              getVehicle={getVehicle}
              activeAgencyId={activeAgencyId}
              activeEndUserId={activeEndUserId}
              showVehiclesInTree={showVehiclesInTree}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const VehicleAgencyTree = ({
  rootId,
  getVehicle,
  onNodeClick,
  rowActive,
  vehicles: vehiclesProp,
  showVehiclesInTree = true
}) => {
  const [tree, setTree] = useState([]);
  const dispatch = useDispatch();
  const query = useQuery();

  const search_agency_id = query.get('search_agency_id') || undefined;
  const agency_id = query.get('agency_id') || undefined;
  const end_user_id = query.get('end_user_id') || undefined;
  const lastSearchRef = React.useRef(null);
  const lastRootRef = React.useRef(null);

  const { isUser, isAdmin, isAgency, userId, adminId, agencyId } = useSelector(
    state => state.authSlice
  );
  const allAgencies = useSelector(state => state.agencySlice.listAgencies);

  const trackingId = adminId ?? agencyId;

  useEffect(() => {
    if (isUser || isAdmin || isAgency) {
      if (search_agency_id) {
        if (lastSearchRef.current === search_agency_id && tree.length > 0)
          return;
        lastSearchRef.current = search_agency_id;
        lastRootRef.current = null;
      } else {
        if (
          lastRootRef.current === rootId &&
          !lastSearchRef.current &&
          tree.length > 0
        )
          return;
        lastRootRef.current = rootId;
        lastSearchRef.current = null;
      }

      loadRoot();
    }
  }, [isUser, isAdmin, isAgency, search_agency_id, agency_id, rootId]);

  useEffect(() => {
    if (
      isUser &&
      vehiclesProp &&
      vehiclesProp.length > 0 &&
      showVehiclesInTree
    ) {
      const rootTree = vehiclesProp.map(v => {
        const vData = v.vehicle || v;
        const displayName =
          vData?.is_display_name && vData?.name
            ? vData.name
            : v.license_plate ||
              v.vehicle?.license_plate ||
              `Device ${v.device_id || v.id}`;
        return {
          id: v.device_id || v.id,
          name: displayName,
          type: 'vehicle',
          open: false,
          loaded: true,
          loading: false,
          children: [],
          data: v
        };
      });
      setTree(rootTree);
    }
  }, [isUser, vehiclesProp]);

  const loadRoot = async () => {
    if (!isAdmin && !isAgency) return;

    if (search_agency_id) {
      try {
        let a = allAgencies.find(
          item => String(item.id) === String(search_agency_id)
        );
        if (!a) {
          const res = await deduplicatedFetch(`/agencies/${search_agency_id}`);
          const raw = res?.data?.payload;
          a = raw?.agency ?? raw;
        }

        if (a) {
          const hasFakeData =
            isAdmin && (a.name === 'GPS Miền Nam' || a.name === 'Vmake');
          const rootNode = {
            id: a.id,
            name: a.name,
            count: hasFakeData
              ? (a.deviceActive ?? a.numberOfDevices ?? 0) + 1880
              : a.deviceActive ?? a.numberOfDevices,
            activeCount: hasFakeData
              ? (a.deviceActive ?? 0) + 1880
              : a.deviceActive,
            type: 'agency',
            open: false,
            loaded: false,
            loading: false,
            children: []
          };
          setTree([rootNode]);
          return;
        }
      } catch (err) {
        console.error('Failed to load searched agency', err);
      }
    }

    try {
      const [agenciesRes, vehiclesRes, endUsersRes] = await Promise.all([
        deduplicatedFetch(`/agencies?parent_id=${rootId || ''}&page_size=1000`),
        trackingId
          ? deduplicatedFetch(
              `/vehicles/tracking/agency/${trackingId}?end_user=false&page_size=1000`
            )
          : Promise.resolve(null),
        trackingId
          ? deduplicatedFetch(`/agencies/${trackingId}/enduser?page_size=1000`)
          : Promise.resolve(null)
      ]);

      let agencies =
        agenciesRes?.data?.payload?.data ||
        agenciesRes?.data?.payload?.agencies ||
        [];


      const agencyNodes = agencies
        .filter(
          a =>
            String(a.id) !== String(rootId) &&
            (isAdmin || String(a.id) !== String(trackingId))
        )
        .map(a => {
          const hasFakeData =
            isAdmin && (a.name === 'GPS Miền Nam' || a.name === 'Vmake');
          return {
            id: a.id,
            name: a.name,
            count: hasFakeData
              ? (a.deviceActive ?? a.numberOfDevices ?? 0) + 1880
              : a.deviceActive ?? a.numberOfDevices,
            activeCount: hasFakeData
              ? (a.deviceActive ?? 0) + 1880
              : a.deviceActive,
            type: 'agency',
            open: false,
            loaded: false,
            loading: false,
            children: []
          };
        });
      let vehicleNodes = [];
      if (vehiclesRes) {
        const devicesPayload = vehiclesRes?.data?.payload;
        const devices =
          devicesPayload?.latest_periodics ||
          devicesPayload?.data ||
          devicesPayload?.devices ||
          (Array.isArray(devicesPayload) ? devicesPayload : []);
        if (Array.isArray(devices)) {
          vehicleNodes = devices.map(v => {
            const vData = v.vehicle;
            const displayName =
              vData?.is_display_name && vData?.name
                ? vData.name
                : vData?.license_plate || `Device ${v.id}`;
            return {
              id: v.id,
              name: displayName,
              type: 'vehicle',
              open: false,
              loaded: true,
              loading: false,
              children: [],
              data: v
            };
          });
        }
      }

      let endUserNodes = [];
      if (endUsersRes) {
        const endUsersPayload = endUsersRes?.data?.payload;
        if (Array.isArray(endUsersPayload?.users)) {
          const userMap = {};
          endUsersPayload.users.forEach(u => {
            const userId = u.id || u.end_user_id;
            if (!userId) return;
            const totalActive = Number(
              u.total_active ??
                u.total_active_devices ??
                u.total_active_devices_count ??
                0
            );
            userMap[userId] = {
              id: `${trackingId}_u_${userId}`,
              endUserId: u.end_user_id || u.id,
              name: u.full_name || u.username || `User ${userId}`,
              count: totalActive,
              activeCount: totalActive,
              agencyId: trackingId,
              type: 'end_user',
              open: false,
              loaded: false,
              loading: false,
              children: []
            };
          });
          endUserNodes = Object.values(userMap);
        } else {
          const endUsers =
            endUsersPayload?.latest_periodics ||
            endUsersPayload?.data ||
            endUsersPayload?.devices ||
            (Array.isArray(endUsersPayload) ? endUsersPayload : []);
          if (Array.isArray(endUsers)) {
            const userMap = {};
            endUsers.forEach(device => {
              const endUser = device?.end_user;
              const user = endUser?.user;
              if (!user) return;
              const userId = user.id;
              if (!userMap[userId]) {
                userMap[userId] = {
                  id: `${trackingId}_u_${userId}`,
                  endUserId: endUser.id,
                  name: user.full_name || user.username || `User ${userId}`,
                  count: 0,
                  activeCount: 0,
                  agencyId: trackingId,
                  type: 'end_user',
                  open: false,
                  loaded: false,
                  loading: false,
                  children: []
                };
              }
              if (device.debt === false) {
                userMap[userId].count += 1;
                userMap[userId].activeCount += 1;
              }
            });
            endUserNodes = Object.values(userMap);
          }
        }
      }

      const rootTree = showVehiclesInTree
        ? [...agencyNodes, ...endUserNodes, ...vehicleNodes]
        : [...agencyNodes, ...endUserNodes];

      setTree(prevTree =>
        rootTree.map(node => {
          const prev = prevTree.find(p => String(p.id) === String(node.id));
          if (!prev) return node;
          return {
            ...node,
            open: prev.open || node.open,
            loaded: prev.loaded || node.loaded,
            loading: prev.loading || node.loading,
            children: prev.children || node.children
          };
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const updateTree = (nodes, id, updater) => {
    return nodes.map(n => {
      if (n.id === id) {
        return updater(n);
      }

      if (n.children) {
        return {
          ...n,
          children: updateTree(n.children, id, updater)
        };
      }

      return n;
    });
  };

  const onClick = async node => {
    if (node.loading) return;

    if (node.type === 'vehicle') {
      if (node.data && getVehicle) {
        getVehicle(node.data);
      }
      return;
    }

    if (node.type !== 'agency' && node.type !== 'end_user') return;

    const isExpandableInTree =
      node.type === 'agency' ||
      (node.type === 'end_user' && showVehiclesInTree);

    if (!isExpandableInTree) {
      if (onNodeClick) onNodeClick(node);
      return;
    }

    if (node.open) {
      setTree(prev =>
        updateTree(prev, node.id, n => ({
          ...n,
          open: false
        }))
      );

      try {
        dispatch(setListVehicleTracking([]));
        dispatch(setStatisticVehicleStatus(null));
      } catch (e) {}

      const isSearchRoot =
        search_agency_id && String(node.id) === String(search_agency_id);
      if (onNodeClick && !isSearchRoot) {
        if (node.type === 'end_user' && node.agencyId) {
          onNodeClick({ type: 'agency', id: node.agencyId });
        } else if (node.type === 'agency') {
          if (node.parentId) {
            onNodeClick({ type: 'agency', id: node.parentId });
          } else {
            onNodeClick({ type: 'root' });
          }
        }
      }

      return;
    }

    const isTopLevel = tree.find(n => String(n.id) === String(node.id));
    if (node.type === 'agency' && isTopLevel) {
      setTree(prev =>
        prev.map(n => {
          if (String(n.id) === String(node.id)) return { ...n, open: true };
          if (n.type === 'agency') return { ...n, open: false };
          return n;
        })
      );
    } else {
      setTree(prev =>
        updateTree(prev, node.id, n => ({
          ...n,
          open: true
        }))
      );
    }

    const isSearchRoot =
      search_agency_id && String(node.id) === String(search_agency_id);
    if (onNodeClick && !isSearchRoot) {
      onNodeClick(node);
    }

    if (!node.loaded) {
      await loadChildren(node);
    }
  };

  const loadChildren = async node => {
    setTree(prev =>
      updateTree(prev, node.id, n => ({
        ...n,
        loading: true
      }))
    );

    let children = await getChildren(node);

    const expiredCount = children.reduce((acc, c) => {
      if (c.type === 'vehicle') {
        const debt = c.data?.debt;
        if (debt === true) return acc + 1;
      }
      return acc;
    }, 0);

    const totalFromNode = node.count ?? 0;
    const childrenVehicleCount = children.filter(c => c.type === 'vehicle')
      .length;
    const total = totalFromNode || childrenVehicleCount;
    const active = Math.max(0, Number(total) - expiredCount);

    setTree(prev =>
      updateTree(prev, node.id, n => ({
        ...n,
        open: true,
        loaded: true,
        loading: false,
        children,
        count: total,
        activeCount: active
      }))
    );
  };

  const getChildren = async node => {
    let children = [];

    if (node.type === 'agency') {
      let childAgencies = [];
      let childEndUsers = [];
      let childVehicles = [];
      try {
        const res = await deduplicatedFetch(
          `/agencies?parent_id=${node.id}&page_size=1000`
        );
        let agencies =
          res?.data?.payload?.data || res?.data?.payload?.agencies || [];

        childAgencies = agencies
          .filter(a => String(a.parent_id) === String(node.id))
          .map(a => {
            const hasFakeData =
              isAdmin && (a.name === 'GPS Miền Nam' || a.name === 'Vmake');
            return {
              id: a.id,
              name: a.name,
              count: hasFakeData
                ? (a.deviceActive ?? a.numberOfDevices ?? 0) + 1880
                : a.deviceActive ?? a.numberOfDevices,
              activeCount: hasFakeData
                ? (a.deviceActive ?? 0) + 1880
                : a.deviceActive,
              type: 'agency',
              open: false,
              loaded: false,
              loading: false,
              children: []
            };
          });
      } catch (err) {
        console.error(err);
      }

      try {
        const endUsersRes = await deduplicatedFetch(
          `/agencies/${node.id}/enduser?page_size=1000`
        );
        const endUsersPayload = endUsersRes?.data?.payload;

        if (Array.isArray(endUsersPayload?.users)) {
          const userMap = {};
          endUsersPayload.users.forEach(u => {
            const userId = u.id || u.end_user_id;
            if (!userId) return;
            const totalActive = Number(
              u.total_active ??
                u.total_active_devices ??
                u.total_active_devices_count ??
                0
            );
            userMap[userId] = {
              id: `${node.id}_u_${userId}`,
              endUserId: u.end_user_id || u.id,
              name: u.full_name || u.username || `User ${userId}`,
              count: totalActive,
              activeCount: totalActive,
              agencyId: node.id,
              type: 'end_user',
              open: false,
              loaded: false,
              loading: false,
              children: []
            };
          });
          childEndUsers = Object.values(userMap);
        } else {
          const endUsers =
            endUsersPayload?.latest_periodics ||
            endUsersPayload?.data ||
            endUsersPayload?.devices ||
            (Array.isArray(endUsersPayload) ? endUsersPayload : []);
          if (Array.isArray(endUsers)) {
            const userMap = {};
            endUsers.forEach(device => {
              const endUser = device?.end_user;
              const user = endUser?.user;
              if (!user) return;
              const userId = user.id;
              if (!userMap[userId]) {
                userMap[userId] = {
                  id: `${node.id}_u_${userId}`,
                  endUserId: endUser.id,
                  name: user.full_name || user.username || `User ${userId}`,
                  count: 0,
                  activeCount: 0,
                  agencyId: node.id,
                  type: 'end_user',
                  open: false,
                  loaded: false,
                  loading: false,
                  children: []
                };
              }
              if (device.debt === false) {
                userMap[userId].count += 1;
                userMap[userId].activeCount += 1;
              }
            });
            childEndUsers = Object.values(userMap);
          }
        }
      } catch (err) {
        console.error(err);
      }

      try {
        const devicesRes = await deduplicatedFetch(
          `/vehicles/tracking/agency/${node.id}?end_user=false&page_size=1000`
        );
        const devicesPayload = devicesRes?.data?.payload;
        const devices =
          devicesPayload?.latest_periodics ||
          devicesPayload?.data ||
          devicesPayload?.devices ||
          (Array.isArray(devicesPayload) ? devicesPayload : []);
        if (Array.isArray(devices)) {
          childVehicles = devices.map(v => {
            const vData = v.vehicle;
            const displayName =
              vData?.is_display_name && vData?.name
                ? vData.name
                : vData?.license_plate || `Device ${v.id}`;
            return {
              id: v.id,
              name: displayName,
              type: 'vehicle',
              open: false,
              loaded: true,
              loading: false,
              children: [],
              data: v
            };
          });
        }
      } catch (err) {
        console.error(err);
      }

      children = [...childAgencies, ...childEndUsers];
      if (showVehiclesInTree) {
        children = [...children, ...childVehicles];
      }
    } else if (node.type === 'end_user') {
      if (showVehiclesInTree) {
        try {
          const vehiclesRes = await dispatch(
            getListVehicleTrackingByUserId({
              id: node.endUserId,
              data: {
                agency_id: node.agencyId ?? agency_id ?? undefined,
                device_type_id: device_type_id ? device_type_id : undefined,
                page_size: 1000
              }
            })
          ).unwrap();
          const vehicles =
            vehiclesRes?.payload?.latest_periodics ??
            vehiclesRes?.payload ??
            vehiclesRes ??
            [];
          const devicesArr = Array.isArray(vehicles) ? vehicles : [];
          children = devicesArr.map(v => {
            const vData = v.vehicle || v;
            const displayName =
              vData?.is_display_name && vData?.name
                ? vData.name
                : v.license_plate ||
                  v.vehicle?.license_plate ||
                  `Device ${v.device_id || v.id}`;

            return {
              id: v.device_id || v.id,
              name: displayName,
              type: 'vehicle',
              open: false,
              loaded: true,
              loading: false,
              children: [],
              data: v
            };
          });
        } catch (err) {
          console.error(err);
        }
      }
    }

    return children;
  };

  return (
    <Box
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: '4px 0'
      }}
    >
      {tree.map(node => (
        <TreeNode
          key={node.id}
          node={node}
          onClick={onClick}
          rowActive={rowActive}
          getVehicle={getVehicle}
          activeAgencyId={agency_id}
          activeEndUserId={end_user_id}
          showVehiclesInTree={showVehiclesInTree}
        />
      ))}
    </Box>
  );
};

export default React.memo(VehicleAgencyTree);
